import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import type { Collection } from 'app/typings/collection';
import { ApiError } from 'app/typings/error';
import type {
  CopywritingForm,
  PackageCover,
  PackageDisclaimer,
  PackageDiscover,
  PackageForm,
} from 'app/typings/packages';
import { isHttpApiError } from 'app/utils/error';
import { apiMutation } from 'app/utils/request/api';

import { CORE_QUERY_KEYS } from '../core/admin/packs';

import {
  listPaginatedPath,
  packageCopywritingPath,
  packageCoverPath,
  packageDisclaimersPath,
  packageDiscoveryPath,
  packagePath,
} from './usePackages';

const savePath = 'packages' as const;
const hidePath = 'packages/:packageId/hide' as const;
const publishPath = 'packages/:packageId/publish' as const;
const deletePath = 'packages/:packageId' as const;
const copywritingPath = 'packages/:packageId/copywriting' as const;
const discoverPath = 'packages/:packageId/discover' as const;
const coverPath = 'packages/:packageId/cover' as const;
const disclaimerPath = 'packages/:packageId/disclaimers' as const;

export function useSavePackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PackageForm) =>
      apiMutation(
        'POST',
        {
          path: savePath,
        },
        payload
      ) as Promise<number>,
    onSuccess: (packageId) => {
      queryClient.invalidateQueries({ queryKey: [packagePath, { packageId }] });
      queryClient.invalidateQueries({ queryKey: [listPaginatedPath] });
      message.success(`Successfully save package`);
    },
    onError: (err) => {
      const isInstanceOfApiError = err instanceof ApiError;

      const publishedError =
        isInstanceOfApiError && err.jsonObject
          ? { ...err.jsonObject }
          : { errorTitle: '', errorMessage: '' };

      const packageError = `Error saving package ${
        publishedError.errorMessage && `: ${publishedError.errorMessage}`
      }`;

      message.error(packageError);
    },
  });
}

export function useHideOldPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { packageId: number; hidden: boolean }) =>
      apiMutation(
        'PATCH',
        {
          path: hidePath,
          params: {
            packageId: payload.packageId.toString(),
          },
        },
        payload
      ) as Promise<{ hidden: boolean }>,
    onSuccess: (_, { packageId, hidden }) => {
      message.success(`Package #${packageId} ${hidden ? 'hidden' : 'shown'}`);

      queryClient.invalidateQueries({ queryKey: [CORE_QUERY_KEYS.packs] });
    },
    onError: (_, { packageId }) => {
      message.error(`Error hiding package ${packageId}`);
    },
  });
}

export function usePublishOldPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { packageId: number; published: boolean }) =>
      apiMutation(
        'PATCH',
        {
          path: publishPath,
          params: {
            packageId: payload.packageId.toString(),
          },
        },
        payload
      ) as Promise<{ published: boolean }>,
    onSuccess: (_, payload) => {
      message.success(
        `Package #${payload.packageId} ${
          payload.published ? 'published' : 'unpublished'
        }`
      );
      queryClient.invalidateQueries({ queryKey: [CORE_QUERY_KEYS.packs] });
    },
    onError: (error, payload) => {
      if (isHttpApiError(error)) {
        return;
      }

      message.error(`Error while modifying package #${payload.packageId}`);
    },
  });
}

export function useDeletePackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { packageId: number }) =>
      apiMutation('DELETE', {
        path: deletePath,
        params: {
          packageId: payload.packageId.toString(),
        },
      }) as Promise<Collection>,
    onSuccess: (_, payload) => {
      message.success(`Package #${payload.packageId} deleted`);
      queryClient.invalidateQueries({ queryKey: [listPaginatedPath] });
    },
    onError: () => {
      message.error(`Not possible: this package is used somewhere else`);
    },
  });
}

export function useSavePackageCopywriting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      packageId,
      payload,
    }: {
      packageId: number;
      payload: CopywritingForm;
    }) =>
      apiMutation(
        'PUT',
        {
          path: copywritingPath,
          params: {
            packageId: packageId.toString(),
          },
        },
        payload
      ) as Promise<number>,
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: [packageCopywritingPath, [packageId]],
      });
      message.success(`Copywriting for package #${packageId} saved`);
    },
    onError: () => {
      message.error(`Error saving Copywriting`);
    },
  });
}

export function useSavePackageDiscover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      packageId,
      payload,
    }: {
      packageId: number;
      payload: PackageDiscover;
    }) =>
      apiMutation(
        'PUT',
        {
          path: discoverPath,
          params: {
            packageId: packageId.toString(),
          },
        },
        payload
      ) as Promise<number>,
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: [packageDiscoveryPath, [packageId]],
      });
      message.success(`Discover for package #${packageId} saved`);
    },
    onError: (err) => {
      message.error(`Error saving Discover`);
    },
  });
}

export function useSavePackageCover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      packageId,
      payload,
    }: {
      packageId: number;
      payload: PackageCover;
    }) =>
      apiMutation(
        'PUT',
        {
          path: coverPath,
          params: {
            packageId: packageId.toString(),
          },
        },
        payload
      ) as Promise<number>,
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: [packageCoverPath, [packageId]],
      });
      message.success(`Covers for package #${packageId} saved`);
    },
    onError: () => {
      message.error(`Error saving Cover`);
    },
  });
}

export function useSavePackageDisclaimer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      packageId,
      payload,
    }: {
      packageId: number;
      payload: PackageDisclaimer[];
    }) =>
      apiMutation(
        'PUT',
        {
          path: disclaimerPath,
          params: {
            packageId: packageId.toString(),
          },
        },
        payload
      ) as Promise<number>,
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: [packageDisclaimersPath, [packageId]],
      });
      message.success(`Disclaimers for package #${packageId} saved`);
    },
    onError: (err) => {
      message.error(`Error saving Disclaimers`);
    },
  });
}
